import { useState, useMemo, useEffect, useRef } from "react";
import { FaMoneyBill, FaPercent, FaClock } from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";
// import BankGradeCard from "./BankGradeCard";
import gsap from "gsap";
import SEO from "./SEO";

export default function LoanCalculator() {
  const [loanAmount, setLoanAmount] = useState(30000);
  const [interestRate, setInterestRate] = useState(1);
  const [loanTerm, setLoanTerm] = useState(30);
  const containerRef = useRef<HTMLDivElement>(null);
  const calculatorRef = useRef<HTMLDivElement>(null);

  const { dailyPayment, totalPayment, totalInterest } = useMemo(() => {
    const principal = loanAmount;
    const dailyInterest = (principal * interestRate) / 100;
    const totalInterest = dailyInterest * loanTerm;
    const totalPayment = principal + totalInterest;
    const dailyPayment = totalPayment / loanTerm;
    return { dailyPayment, totalPayment, totalInterest };
  }, [loanAmount, interestRate, loanTerm]);

  useEffect(() => {
    const cards = document.querySelectorAll(".result-card");
    const ctx = gsap.context(() => {
      gsap.from(calculatorRef.current, {
        y: 60,
        opacity: 0,
        duration: 1.2,
        ease: "power4.out",
        delay: 0.2,
        clearProps: "all",
      });

      gsap.from(cards, {
        y: 20,
        opacity: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out",
        delay: 0.6,
        clearProps: "all",
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-white text-text-primary font-sans selection:bg-primary-light selection:text-primary">
      <SEO
        title="Loan Calculator"
        description={`Debt calculator by ${import.meta.env.VITE_COMPANY_NAME}. Easy estimation for your real-time liquidity planning. Compare loan amounts, term lengths, and interest rates.`}
        keywords={`loan calculator India, debt calculator, interest rate calculator, personal loan planner, ${import.meta.env.VITE_COMPANY_NAME} tools`}
      />
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-gradient-to-b from-white via-blue-50/50 to-white" />
      <div className="relative z-20">
        <Navbar />
      </div>

      <section className="w-full pt-40 pb-20 text-center px-6 relative overflow-hidden mb-12">
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-white border border-blue-100 shadow-sm rounded-full mb-8">
            <div className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest text-text-secondary uppercase">Financial Planning Tool</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-8 text-text-primary">
            Smart Loan <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Calculator.</span>
          </h1>
          <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
            Configure your required amount and estimate your installments instantly.
          </p>
        </div>
      </section>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 mb-32">

        <div
          ref={calculatorRef}
          className="relative bg-white border border-gray-100 p-8 md:p-16 shadow-xl rounded-[2rem]"
        >
          {/* Sliders Container */}
          <div className="flex flex-col gap-12 w-full relative z-10">
            <Slider
              icon={<FaMoneyBill className="text-primary" />}
              label="Loan Amount Needed"
              value={loanAmount}
              min={5000}
              max={100000}
              step={5000}
              onChange={setLoanAmount}
              unit="₹"
            />
            <Slider
              icon={<FaPercent className="text-primary" />}
              label="Estimated Interest Rate"
              value={interestRate}
              min={0}
              max={2}
              step={0.5}
              onChange={setInterestRate}
              unit="%"
            />
            <Slider
              icon={<FaClock className="text-primary" />}
              label="Loan Tenure"
              value={loanTerm}
              min={1}
              max={90}
              step={1}
              onChange={setLoanTerm}
              unit="Days"
            />
          </div>

          {/* Results Panel */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mt-16 relative z-10">
            {[
              { label: "Daily Installment", value: dailyPayment, sub: "estimated amount per day" },
              { label: "Total Settlement", value: totalPayment, sub: "full cycle amount" },
              { label: "Total Interest", value: totalInterest, sub: "estimated accrued fee" },
            ].map(({ label, value, sub }) => (
              <div
                key={label}
                className={`result-card bg-blue-50/50 border border-blue-100 rounded-[1.5rem] p-8 flex flex-col gap-6 transition-all hover:bg-blue-50`}
              >
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-text-secondary uppercase mb-1">{label}</span>
                  <span className="text-xs font-semibold text-primary">{sub}</span>
                </div>

                <div className="text-3xl lg:text-4xl font-extrabold text-text-primary flex items-baseline gap-1 mt-auto">
                  <span className="text-lg text-primary">₹</span>
                  {value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  <span className="text-sm text-text-secondary">.{(value % 1).toFixed(2).substring(2)}</span>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* <BankGradeCard /> */}
      <Footer />
    </div>
  );
}

const Slider = ({ icon, label, value, onChange, min, max, step, unit }: any) => (
  <div className="flex flex-col w-full group">
    <div className="flex justify-between items-end mb-6 border-b border-gray-200 pb-4 transition-all group-hover:border-primary">
      <div className="flex items-center gap-6">
        <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-2xl transition-all group-hover:bg-primary/10 group-hover:scale-105">
          {icon}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-bold text-text-secondary uppercase mb-1 tracking-wider">{label}</span>
          <div className="text-3xl font-extrabold text-text-primary flex items-baseline gap-1">
            {unit === "₹" && <span className="text-xl text-primary">₹</span>}
            {value.toLocaleString()}
            {unit !== "₹" && <span className="text-lg text-text-secondary ml-1">{unit}</span>}
          </div>
        </div>
      </div>
      <div className="hidden sm:block text-xs font-bold text-text-secondary/50 uppercase tracking-wider">
        Range: {min} - {max}
      </div>
    </div>

    <div className="relative h-12 flex items-center">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="calculator-slider w-full h-2 appearance-none cursor-pointer outline-none bg-gray-100 rounded-full"
        style={{
          background: `linear-gradient(to right, var(--primary) ${((value - min) / (max - min)) * 100}%, transparent ${((value - min) / (max - min)) * 100}%)`,
        }}
      />
      <style>{`
        .calculator-slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: white;
          border: 4px solid var(--primary);
          cursor: pointer;
          transition: transform 0.2s ease;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        .calculator-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  </div>
);

